import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { firstValueFrom } from 'rxjs';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import {
  ColumnDefinition,
  MasterTableComponent,
  TableAction,
  TableFilterDefinition,
} from '@shared/components/master-table/master-table.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { DeviceRecord, DevicesResponse } from '../../devices/device.interface';
import { EmployeeRecord } from './employee.interface';
import { UserConfig, UserPasswordResetResponse, UserUpdatePayload } from '../../config/users/user.interface';
import { AppConfigService } from 'app/AppConfigService.service';
import { EmployeeImportDialogComponent } from './employee-import-dialog.component';
import {
  EmployeeFingerprintEnrollmentDialogComponent,
  FingerprintEnrollmentDialogResult,
} from './employee-fingerprint-enrollment-dialog.component';
import {
  DynamicDialogField,
  FormDialogComponent,
} from '../../config/create-permission/dialog/form-dialog/form-dialog.component';
import { EmployeeTemporaryCredentialsDialogComponent } from './employee-temporary-credentials-dialog.component';
import { buildOrgUnitTreeOptions } from '@shared/utils/org-unit-tree.util';

interface CompanyRecord {
  companiesRecordId: string;
  center: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface CompaniesResponse {
  data?: CompanyRecord[];
  items?: CompanyRecord[];
  results?: CompanyRecord[];
  companiesRecords?: CompanyRecord[];
}

interface CompanyLookupResponse {
  entity?: CompanyRecord;
}

interface OrgUnitRecord {
  orgUnitId: string;
  companiesRecordId: string;
  code?: string;
  name: string;
  type?: string;
  parentId?: string | null;
  orgPath?: string | null;
  isActive?: boolean;
}

interface OrgUnitsResponse {
  data?: OrgUnitRecord[];
  items?: OrgUnitRecord[];
  results?: OrgUnitRecord[];
  orgUnits?: OrgUnitRecord[];
}

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
  total?: number;
  totalItems?: number;
  count?: number;
  page?: number;
  perPage?: number;
  pagination?: {
    page?: number;
    perPage?: number;
    total?: number;
    totalPages?: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
  };
}

interface EmployeeExportPayload {
  mode: 'selected_ids' | 'current_page' | 'all_filtered';
  ids?: string[];
  companiesRecordId?: string;
  orgUnitId?: string;
  orgUnitIds?: string[];
  isActive?: 'true' | 'false';
  search?: string;
  employeeNumber?: string;
  page: number;
  perPage: number;
}

@Component({
  selector: 'app-employees',
  imports: [BreadcrumbComponent, MasterTableComponent, TranslateModule, FormsModule],
  templateUrl: './employees.component.html',
  styleUrl: './employees.component.scss',
})
export class EmployeesComponent implements OnInit {
  @ViewChild('employeesTable') employeesTable?: MasterTableComponent<EmployeeRecord>;
  dataSource = new MatTableDataSource<EmployeeRecord>([]);
  companies: CompanyRecord[] = [];
  orgUnits: OrgUnitRecord[] = [];
  users: UserConfig[] = [];
  isLoading = true;
  actions: TableAction<EmployeeRecord>[] = [];
  columnDefinitions: ColumnDefinition[] = [];
  tableFilters: TableFilterDefinition<EmployeeRecord>[] = [];
  searchValue = '';
  activeFilters: Record<string, string | number | boolean | null | Array<string | number | boolean>> = {};
  currentPage = 1;
  pageSize = 15;
  totalItems = 0;
  private loadRequestSequence = 0;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appConfigService = inject(AppConfigService);
  private searchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private readonly attemptedCompanyLookups = new Set<string>();

  ngOnInit(): void {
    this.configureTranslations();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureTranslations();
      this.dataSource.data = this.dataSource.data.map((employee) => this.normalizeEmployee(employee));
      this.buildTableFilters();
    });
    void this.initializeData();
  }

  async initializeData(): Promise<void> {
    this.isLoading = true;
    try {
      void this.loadUsers();
      await Promise.all([
        this.loadCompanies(),
        this.loadOrgUnits(),
      ]);
      this.buildTableFilters();
      await this.loadData(false);
    } finally {
      this.isLoading = false;
    }
  }

  async loadData(showLoading = true): Promise<void> {
    const requestId = ++this.loadRequestSequence;
    if (showLoading) {
      this.isLoading = true;
    }
    try {
      const requestedPage = this.currentPage;
      const requestedPageSize = this.pageSize;
      const parsedResponse = await this.loadEmployeesPage(requestedPage, requestedPageSize);
      const normalizedResponse = await this.expandEmployeesPageIfBackendCapsPageSize(
        parsedResponse,
        requestedPage,
        requestedPageSize
      );
      if (requestId !== this.loadRequestSequence) {
        return;
      }
      const items = normalizedResponse.items;
      await this.loadMissingCompanies(items);
      if (requestId !== this.loadRequestSequence) {
        return;
      }
      this.dataSource.data = items.map((employee) => this.normalizeEmployee(employee));
      this.totalItems = normalizedResponse.total;
      this.currentPage = requestedPage;
      this.pageSize = requestedPageSize;
      this.buildTableFilters();
    } catch (error) {
      if (requestId !== this.loadRequestSequence) {
        return;
      }
      console.error('Error loading employees', error);
      this.dataSource.data = [];
      this.totalItems = 0;
    } finally {
      if (showLoading && requestId === this.loadRequestSequence) {
        this.isLoading = false;
      }
    }
  }

  private async loadEmployeesPage(page: number, perPage: number): Promise<{
    items: EmployeeRecord[];
    total: number;
    page: number;
    perPage: number;
  }> {
    const activeOrgUnitIds = this.getEffectiveOrgUnitIds();
    const response = await firstValueFrom(
      this.apiService.get<EmployeesResponse | EmployeeRecord[]>('employees', {
        params: {
          page,
          perPage,
          ...(this.searchValue ? { search: this.searchValue } : {}),
          ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
          ...(activeOrgUnitIds.length
            ? activeOrgUnitIds.length === 1
              ? { orgUnitId: activeOrgUnitIds[0] }
              : { orgUnitIds: activeOrgUnitIds }
            : {}),
          ...(this.getActiveStatus() !== '' ? { isActive: this.getActiveStatus() } : {}),
        },
      })
    );

    return this.parseEmployeesResponse(response);
  }

  private async expandEmployeesPageIfBackendCapsPageSize(
    firstResponse: {
      items: EmployeeRecord[];
      total: number;
      page: number;
      perPage: number;
    },
    requestedPage: number,
    requestedPageSize: number
  ): Promise<{
    items: EmployeeRecord[];
    total: number;
    page: number;
    perPage: number;
  }> {
    const backendPageSize = firstResponse.perPage;
    if (
      !backendPageSize ||
      backendPageSize >= requestedPageSize ||
      firstResponse.items.length >= requestedPageSize ||
      firstResponse.items.length >= firstResponse.total
    ) {
      return firstResponse;
    }

    const targetOffset = (requestedPage - 1) * requestedPageSize;
    const firstBackendPage = Math.floor(targetOffset / backendPageSize) + 1;
    const skipFromFirstBackendPage = targetOffset % backendPageSize;
    const aggregated: EmployeeRecord[] = [];
    let backendPage = firstBackendPage;
    let total = firstResponse.total;

    while (aggregated.length < requestedPageSize && aggregated.length + targetOffset < total) {
      const pageResponse =
        backendPage === firstResponse.page && skipFromFirstBackendPage === 0
          ? firstResponse
          : await this.loadEmployeesPage(backendPage, backendPageSize);

      total = pageResponse.total;
      const pageItems = backendPage === firstBackendPage
        ? pageResponse.items.slice(skipFromFirstBackendPage)
        : pageResponse.items;

      aggregated.push(...pageItems);

      if (pageItems.length < backendPageSize || aggregated.length + targetOffset >= total) {
        break;
      }

      backendPage += 1;
    }

    return {
      items: aggregated.slice(0, requestedPageSize),
      total,
      page: requestedPage,
      perPage: requestedPageSize,
    };
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/organization/employees/new']);
  }

  async handleView(row: EmployeeRecord): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/organization/employees', row.employeeId]);
  }

  async handleEdit(row: EmployeeRecord): Promise<void> {
    if (!this.hasAccess('update')) {
      return;
    }
    await this.router.navigate(['/organization/employees', row.employeeId, 'edit']);
  }

  handleDelete(row: EmployeeRecord): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('employeesCatalog.dialog.deleteTitle'),
        message: this.t('employeesCatalog.dialog.deleteMessage', { employee: row.fullName || row.employeeNumber }),
        confirmLabel: this.t('employeesCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`employees/${row.employeeId}`));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('employeesCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting employee', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('employeesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: EmployeeRecord[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(rows.map((row) => firstValueFrom(this.apiService.delete(`employees/${row.employeeId}`))));
        await this.loadData();
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting employees', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('employeesCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleAction(event: { action: TableAction<EmployeeRecord>; selectedRows: EmployeeRecord[] }): void {
    if (event.action.id === 'import') {
      this.handleImport();
      return;
    }

    if (event.action.id === 'export') {
      void this.handleExport(event.selectedRows);
      return;
    }

    if (event.action.behavior) {
      return;
    }

    if (event.action.id === 'details' && event.selectedRows.length === 1) {
      void this.handleView(event.selectedRows[0]);
      return;
    }

    if (event.action.id === 'fingerprint' && event.selectedRows.length === 1) {
      void this.handleFingerprintEnrollment(event.selectedRows[0]);
      return;
    }

    if (event.action.id === 'link-user' && event.selectedRows.length === 1) {
      void this.handleLinkUser(event.selectedRows[0]);
      return;
    }

    if (event.action.id === 'reset-password' && event.selectedRows.length === 1) {
      void this.handleResetPassword(event.selectedRows[0]);
    }
  }

  private handleImport(): void {
    if (!this.hasAccess('import')) {
      return;
    }

    this.employeesTable?.closeFilterOverlays();

    const numbering = this.appConfigService.getCenterConfig()?.employeeNumbering;
    const mode = numbering?.mode === 'auto' || numbering?.mode === 'hybrid' ? numbering.mode : 'manual';
    const padLength = Number(numbering?.padLength || 0);
    const dialogRef = this.dialog.open(EmployeeImportDialogComponent, {
      width: '1180px',
      maxWidth: '96vw',
      maxHeight: '92vh',
      panelClass: 'employee-import-dialog-panel',
      autoFocus: false,
      disableClose: true,
      data: {
        companies: this.companies.filter((company) => company.isActive !== false),
        orgUnits: this.orgUnits.filter((unit) => unit.isActive !== false),
        employeeNumberingMode: mode,
        employeeNumberPadLength: Number.isFinite(padLength) && padLength > 0 ? padLength : 0,
      },
    });

    dialogRef.afterClosed().subscribe((createdEmployees: boolean | undefined) => {
      if (createdEmployees) {
        this.currentPage = 1;
        void this.loadData();
      }
    });
  }

  onSearchChange(search: string): void {
    this.searchValue = search.trim();
    this.currentPage = 1;
    if (this.searchDebounceHandle) {
      clearTimeout(this.searchDebounceHandle);
    }
    this.searchDebounceHandle = setTimeout(() => {
      void this.loadData();
    }, 300);
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null | Array<string | number | boolean>>): void {
    const previousCompany = this.getActiveCompanyId();
    this.activeFilters = filters;
    const currentCompany = this.getActiveCompanyId();

    if (previousCompany !== currentCompany) {
      this.activeFilters['orgUnit'] = null;
    }

    this.currentPage = 1;
    this.buildTableFilters();
    void this.loadData();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    void this.loadData();
  }

  private async loadCompanies(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.apiService.get<CompanyRecord[] | CompaniesResponse>('companies-records', {
          params: { isActive: 'true' },
        })
      );
      this.companies = this.extractCompanies(response);
    } catch (error) {
      console.error('Error loading companies for employees', error);
      this.companies = [];
    }
  }

  private async loadMissingCompanies(employees: Partial<EmployeeRecord>[]): Promise<void> {
    const knownCompanyIds = new Set(
      this.companies.map((company) => (company.companiesRecordId || '').trim()).filter(Boolean)
    );
    const missingCompanyIds = Array.from(
      new Set(
        employees
          .map((employee) => (employee.companiesRecordId || '').trim())
          .filter((companyId) => companyId && !knownCompanyIds.has(companyId))
      )
    ).filter((companyId) => !this.attemptedCompanyLookups.has(companyId));

    if (!missingCompanyIds.length) {
      return;
    }

    missingCompanyIds.forEach((companyId) => this.attemptedCompanyLookups.add(companyId));
    const resolvedCompanies = await Promise.all(
      missingCompanyIds.map(async (companyId): Promise<CompanyRecord | null> => {
        try {
          const response = await firstValueFrom(
            this.apiService.get<CompanyLookupResponse>(
              `companies-records/${encodeURIComponent(companyId)}/with-operational-config`
            )
          );
          return response?.entity?.companiesRecordId ? response.entity : null;
        } catch (error) {
          console.warn(`Could not resolve company ${companyId} for the employee list`, error);
          return null;
        }
      })
    );

    const existingIds = new Set(this.companies.map((company) => (company.companiesRecordId || '').trim()));
    for (const company of resolvedCompanies) {
      const companyId = company?.companiesRecordId?.trim();
      if (company && companyId && !existingIds.has(companyId)) {
        this.companies.push(company);
        existingIds.add(companyId);
      }
    }
  }

  private async loadOrgUnits(): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<OrgUnitRecord[] | OrgUnitsResponse>('org-units'));
      this.orgUnits = this.extractOrgUnits(response);
    } catch (error) {
      console.error('Error loading org units for employees', error);
      this.orgUnits = [];
    }
  }

  private async loadUsers(): Promise<void> {
    try {
      const response = await firstValueFrom(this.apiService.get<UserConfig[]>('users'));
      this.users = (response || []).map((user) => ({
        ...user,
        displayName: user.displayName || '',
        email: user.email || '',
        employeeId: user.employeeId || '',
        status: user.status || 'active',
      }));
    } catch (error) {
      console.error('Error loading users for employees', error);
      this.users = [];
    }
  }

  private extractCompanies(response: CompanyRecord[] | CompaniesResponse): CompanyRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.companiesRecords || [];
  }

  private extractOrgUnits(response: OrgUnitRecord[] | OrgUnitsResponse): OrgUnitRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.orgUnits || [];
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }

    return response.items || response.data || response.results || response.employees || [];
  }

  private parseEmployeesResponse(response: EmployeeRecord[] | EmployeesResponse): {
    items: EmployeeRecord[];
    total: number;
    page: number;
    perPage: number;
  } {
    if (Array.isArray(response)) {
      return {
        items: response,
        total: response.length,
        page: this.currentPage,
        perPage: this.pageSize,
      };
    }

    const items = this.extractEmployees(response);
    return {
      items,
      total: response.pagination?.total ?? response.total ?? response.totalItems ?? response.count ?? items.length,
      page: response.pagination?.page ?? response.page ?? this.currentPage,
      perPage: response.pagination?.perPage ?? response.perPage ?? this.pageSize,
    };
  }

  private normalizeEmployee(employee: Partial<EmployeeRecord>): EmployeeRecord {
    const status = (employee.status || (employee.isActive === false ? 'inactive' : 'active')).toLowerCase();
    const isActive = employee.isActive !== false && status !== 'inactive';
    const employeeCompanyId = (employee.companiesRecordId || '').trim();
    const employeeOrgUnitId = (employee.orgUnitId || '').trim();
    const orgUnit = this.orgUnits.find((unit) => (unit.orgUnitId || '').trim() === employeeOrgUnitId);
    const resolvedCompanyId = employeeCompanyId || (orgUnit?.companiesRecordId || '').trim();
    const company = this.companies.find((item) => (item.companiesRecordId || '').trim() === resolvedCompanyId);
    const companyName = company?.name?.trim() || employee.companyName?.trim();

    return {
      employeeId: employee.employeeId || crypto.randomUUID(),
      center: employee.center || '',
      employeeNumber: employee.employeeNumber || '',
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      secondLastName: employee.secondLastName || '',
      fullName: employee.fullName || [employee.firstName, employee.lastName, employee.secondLastName].filter(Boolean).join(' '),
      email: employee.email || '',
      phone: employee.phone || '',
      companiesRecordId: resolvedCompanyId,
      orgUnitId: employeeOrgUnitId,
      orgPath: employee.orgPath || '',
      userUid: employee.userUid || '',
      status,
      isActive,
      companyName: companyName || this.t(
        resolvedCompanyId
          ? 'employeesCatalog.fields.companyUnavailable'
          : 'employeesCatalog.fields.companyUnassigned'
      ),
      orgUnitName: orgUnit?.name || employee.orgUnitName?.trim() || '',
      statusLabel: status === 'inactive' ? this.t('common.status.inactive') : this.t('common.status.active'),
    };
  }

  private buildTableFilters(): void {
    const companyOptions = this.companies.map((company) => ({
      value: company.companiesRecordId,
      label: company.name,
    }));
    const filters: TableFilterDefinition<EmployeeRecord>[] = [
      {
        id: 'company',
        label: this.t('employeesCatalog.filters.company'),
        placeholder: this.t('employeesCatalog.filters.companyPlaceholder'),
        icon: 'business',
        searchable: true,
        value: this.activeFilters['company'] ?? null,
        options: companyOptions,
      },
    ];

    if (this.getActiveCompanyId()) {
      filters.push({
        id: 'orgUnit',
        type: 'tree-select',
        label: this.t('employeesCatalog.fields.orgUnit'),
        placeholder: this.t('employeesCatalog.filters.orgUnitPlaceholder'),
        searchPlaceholder: this.t('employeesCatalog.orgUnitPicker.searchPlaceholder'),
        icon: 'account_tree',
        searchable: true,
        multiple: true,
        value: this.activeFilters['orgUnit'] ?? null,
        options: this.getOrgUnitsForActiveCompany().map((unit) => ({
          value: unit.orgUnitId,
          label: unit.name,
        })),
        treeOptions: this.buildOrgUnitFilterTree(),
      });
    }

    filters.push(
      {
        id: 'status',
        label: this.t('employeesCatalog.filters.status'),
        placeholder: this.t('employeesCatalog.filters.statusPlaceholder'),
        icon: 'verified',
        searchable: false,
        value: this.activeFilters['status'] ?? null,
        options: [
          { value: 'true', label: this.t('common.status.active') },
          { value: 'false', label: this.t('common.status.inactive') },
        ],
      }
    );

    this.tableFilters = filters;
  }

  private configureTranslations(): void {
    this.actions = [
      {
        id: 'add',
        icon: 'add',
        tooltip: this.t('employeesCatalog.actions.create'),
        colorClass: 'success',
        auth: { resource: 'employees', action: 'create' },
        behavior: 'add',
      },
      {
        id: 'details',
        icon: 'visibility',
        tooltip: this.t('employeesCatalog.actions.view'),
        colorClass: 'info',
        auth: { resource: 'employees', action: 'read' },
        selectionMode: 'single',
      },
      {
        id: 'import',
        icon: 'upload_file',
        tooltip: this.t('employeesCatalog.actions.importEmployees'),
        colorClass: 'primary',
        auth: { resource: 'employees', action: 'import' },
        selectionMode: 'none',
      },
      {
        id: 'edit',
        icon: 'edit',
        tooltip: this.t('employeesCatalog.actions.edit'),
        colorClass: 'accent',
        auth: { resource: 'employees', action: 'update' },
        behavior: 'edit',
        selectionMode: 'single',
      },
      {
        id: 'fingerprint',
        icon: 'fingerprint',
        tooltip: this.t('employeesCatalog.devices.enrollFingerprint'),
        colorClass: 'primary',
        auth: { resource: 'devices', action: 'update' },
        selectionMode: 'single',
      },
      {
        id: 'link-user',
        icon: 'person_add',
        tooltip: this.t('employeesCatalog.actions.linkUser'),
        colorClass: 'info',
        auth: { resource: 'users', action: 'update' },
        selectionMode: 'single',
      },
      {
        id: 'reset-password',
        icon: 'password',
        tooltip: this.t('employeesCatalog.resetPassword'),
        colorClass: 'info',
        auth: { resource: 'users', action: 'reset-password' },
        selectionMode: 'single',
        predicate: (selection) => !!this.getLinkedUserUid(selection[0]),
      },
      {
        id: 'export',
        icon: 'download',
        tooltip: this.t('employeesCatalog.actions.exportEmployees'),
        colorClass: 'info',
        auth: { resource: 'employees', action: 'read' },
        selectionMode: 'none',
      },
      {
        id: 'delete',
        icon: 'delete',
        tooltip: this.t('employeesCatalog.actions.delete'),
        colorClass: 'danger',
        auth: { resource: 'employees', action: 'delete' },
        behavior: 'delete',
        selectionMode: 'single',
      },
    ];

    this.columnDefinitions = [
      { def: 'select', label: this.t('common.select'), type: 'check', visible: true },
      { def: 'employeeNumber', label: this.t('employeesCatalog.fields.employeeNumber'), type: 'text', visible: true },
      { def: 'fullName', label: this.t('employeesCatalog.fields.fullName'), type: 'text', visible: true },
      { def: 'companyName', label: this.t('employeesCatalog.fields.company'), type: 'text', visible: true },
      { def: 'email', label: this.t('employeesCatalog.fields.email'), type: 'email', visible: true },
      { def: 'phone', label: this.t('employeesCatalog.fields.phone'), type: 'phone', visible: true },
      { def: 'orgUnitName', label: this.t('employeesCatalog.fields.orgUnit'), type: 'text', visible: true },
      {
        def: 'statusLabel',
        label: this.t('employeesCatalog.fields.status'),
        type: 'status',
        visible: true,
      },
    ];

    this.buildTableFilters();
  }

  private getActiveCompanyId(): string {
    return (this.activeFilters['company'] as string) || '';
  }

  private getActiveStatus(): string {
    const value = this.activeFilters['status'];
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return String(value);
  }

  private getActiveOrgUnitIds(): string[] {
    const value = this.activeFilters['orgUnit'];
    if (Array.isArray(value)) {
      return value.map(String).filter(Boolean);
    }

    return value ? [String(value)] : [];
  }

  private getEffectiveOrgUnitIds(): string[] {
    const selectedIds = this.getActiveOrgUnitIds();
    if (selectedIds.length < 2) {
      return selectedIds;
    }

    const selectedIdSet = new Set(selectedIds);
    const parentById = new Map(
      this.getOrgUnitsForActiveCompany().map((unit) => [unit.orgUnitId, unit.parentId || ''])
    );

    return selectedIds.filter((orgUnitId) => {
      const visitedIds = new Set<string>();
      let parentId = parentById.get(orgUnitId) || '';

      while (parentId && !visitedIds.has(parentId)) {
        if (selectedIdSet.has(parentId)) {
          return false;
        }
        visitedIds.add(parentId);
        parentId = parentById.get(parentId) || '';
      }

      return true;
    });
  }

  private getOrgUnitsForActiveCompany(): OrgUnitRecord[] {
    const companyId = this.getActiveCompanyId();
    if (!companyId) {
      return [];
    }

    return this.orgUnits
      .filter((unit) => unit.companiesRecordId === companyId && unit.isActive !== false)
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  private buildOrgUnitFilterTree() {
    return buildOrgUnitTreeOptions(this.getOrgUnitsForActiveCompany());
  }

  private buildExportPayload(
    selectedRows: EmployeeRecord[] = [],
    requestedMode?: EmployeeExportPayload['mode']
  ): EmployeeExportPayload {
    const status = this.getActiveStatus() as 'true' | 'false' | '';
    const activeOrgUnitIds = this.getEffectiveOrgUnitIds();
    const selectedIds = Array.from(new Set(selectedRows.map((row) => row.employeeId).filter(Boolean)));
    const mode = requestedMode || this.resolveExportMode(selectedIds.length);
    const employeeNumber = String(this.activeFilters['employeeNumber'] || '').trim();

    return {
      mode,
      ...(mode === 'selected_ids' ? { ids: selectedIds } : {}),
      ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
      ...(activeOrgUnitIds.length === 1 ? { orgUnitId: activeOrgUnitIds[0] } : {}),
      ...(activeOrgUnitIds.length > 1 ? { orgUnitIds: activeOrgUnitIds } : {}),
      ...(status ? { isActive: status } : {}),
      ...(this.searchValue.trim() ? { search: this.searchValue.trim() } : {}),
      ...(employeeNumber ? { employeeNumber } : {}),
      page: this.currentPage,
      perPage: this.pageSize,
    };
  }

  private resolveExportMode(selectedCount: number): EmployeeExportPayload['mode'] {
    if (selectedCount > 0) {
      return 'selected_ids';
    }

    const hasActiveFilters = !!(
      this.searchValue.trim()
      || this.getActiveCompanyId()
      || this.getActiveOrgUnitIds().length > 0
      || this.getActiveStatus()
      || String(this.activeFilters['employeeNumber'] || '').trim()
    );

    return hasActiveFilters ? 'all_filtered' : 'current_page';
  }

  private async handleExport(
    selectedRows: EmployeeRecord[] = [],
    requestedMode?: EmployeeExportPayload['mode']
  ): Promise<void> {
    this.isLoading = true;
    try {
      const response = (await firstValueFrom(
        this.apiService.postBlob('employees/export', this.buildExportPayload(selectedRows, requestedMode), {
          observe: 'response',
        })
      )) as HttpResponse<Blob>;

      this.downloadExportBlob(
        response.body || new Blob(),
        this.resolveExportFileName(response) || `employees-${new Date().toISOString().slice(0, 10)}.xlsx`
      );

      this.showNotification('snackbar-success', this.t('employeesCatalog.notifications.exported'), 'bottom', 'center');
    } catch (error) {
      console.error('Error exporting employees', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('employeesCatalog.notifications.exportError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private resolveExportFileName(response: HttpResponse<Blob>): string | null {
    const header = response.headers.get('content-disposition') || response.headers.get('Content-Disposition');
    if (!header) {
      return null;
    }

    const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]).replace(/["]/g, '');
    }

    const fileNameMatch = header.match(/filename="?([^"]+)"?/i);
    return fileNameMatch?.[1] || null;
  }

  private downloadExportBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private async handleFingerprintEnrollment(row: EmployeeRecord): Promise<void> {
    if (!row.employeeNumber.trim()) {
      this.showNotification('snackbar-warning', this.t('employeesCatalog.notifications.validationFingerprintEmployeeNumber'), 'bottom', 'center');
      return;
    }

    const devices = await this.loadDevicesForCompany(row.companiesRecordId);
    if (!devices.length) {
      this.showNotification('snackbar-warning', this.t('employeesCatalog.notifications.validationFingerprintDevice'), 'bottom', 'center');
      return;
    }

    const dialogRef = this.dialog.open(EmployeeFingerprintEnrollmentDialogComponent, {
      width: '980px',
      maxWidth: '96vw',
      data: {
        employeeId: row.employeeId,
        employeeName: row.fullName || row.employeeNumber,
        employeeNumber: row.employeeNumber,
        devices: devices.map((device) => ({
          deviceId: device.deviceId,
          name: device.name || device.serial || '-',
          serial: device.serial,
        })),
      },
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe(async (result: FingerprintEnrollmentDialogResult | undefined) => {
      if (!result) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(
          this.apiService.post(`devices/${result.deviceId}/fingerprint-enrollments`, {
            employeeNumber: result.employeeNumber,
            fingerId: result.fingerId,
            retry: result.retry,
            overwrite: result.overwrite,
          })
        );
        this.showNotification(
          'snackbar-success',
          this.t('employeesCatalog.notifications.fingerprintQueued', { fingerId: result.fingerId }),
          'bottom',
          'center'
        );
      } catch (error) {
        console.error('Error queueing fingerprint enrollment from list', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('employeesCatalog.notifications.fingerprintQueueError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  private async handleLinkUser(row: EmployeeRecord): Promise<void> {
    if (!this.authorizationService.hasAccess({ resource: 'users', action: 'update' })) {
      return;
    }

    const currentLinkedUser =
      this.users.find((user) => user.uid === row.userUid) ||
      this.users.find((user) => user.employeeId === row.employeeId);
    const fields: DynamicDialogField[] = [
      {
        key: 'userUid',
        label: this.t('employeesCatalog.fields.relatedUser'),
        type: 'select',
        required: false,
        fullWidth: true,
        hint: this.t('employeesCatalog.fields.relatedUserHint'),
        options: this.getUserLinkOptions(row.employeeId, currentLinkedUser?.uid || ''),
      },
    ];

    const dialogRef = this.dialog.open(FormDialogComponent, {
      width: '36rem',
      maxWidth: '96vw',
      data: {
        action: 'edit',
        title: this.t('employeesCatalog.dialog.linkUserTitle'),
        submitLabel: this.t('common.saveChanges'),
        fields,
        value: {
          userUid: currentLinkedUser?.uid || '',
        },
      },
      autoFocus: false,
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (!result) {
      return;
    }

    const selectedUserUid = typeof result.userUid === 'string' ? result.userUid.trim() : '';
    if ((currentLinkedUser?.uid || '') === selectedUserUid) {
      return;
    }

    this.isLoading = true;
    try {
      if (currentLinkedUser?.uid && currentLinkedUser.uid !== selectedUserUid) {
        await firstValueFrom(
          this.apiService.patch<UserConfig>(`users/${currentLinkedUser.uid}`, { employeeId: null } as unknown as UserUpdatePayload)
        );
      }

      if (selectedUserUid) {
        await firstValueFrom(
          this.apiService.patch<UserConfig>(`users/${selectedUserUid}`, { employeeId: row.employeeId })
        );
      }

      await Promise.all([this.loadUsers(), this.loadData()]);
      this.showNotification(
        'snackbar-success',
        this.t('employeesCatalog.notifications.userLinked'),
        'bottom',
        'center'
      );
    } catch (error) {
      console.error('Error linking user to employee', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('employeesCatalog.notifications.userLinkedError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private async handleResetPassword(row: EmployeeRecord): Promise<void> {
    if (!this.authorizationService.hasAccess({ resource: 'users', action: 'reset-password' })) {
      return;
    }

    const userUid = this.getLinkedUserUid(row);
    if (!userUid) {
      return;
    }

    const linkedUser = this.users.find((user) => user.uid === userUid);
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '28rem',
      maxWidth: '94vw',
      data: {
        title: this.t('employeesCatalog.dialog.resetPasswordTitle'),
        message: this.t('employeesCatalog.dialog.resetPasswordMessage', {
          employee: row.fullName || row.employeeNumber,
        }),
        confirmLabel: this.t('employeesCatalog.dialog.resetPasswordConfirm'),
        cancelLabel: this.t('common.cancel'),
        confirmIcon: 'password',
      },
      autoFocus: false,
    });

    const confirmed = await firstValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.patch<UserPasswordResetResponse>(
          `users/${encodeURIComponent(userUid)}/password`,
          null
        )
      );
      if (!response.updated || !response.temporaryPassword) {
        throw new Error(this.t('employeesCatalog.notifications.resetPasswordInvalidResponse'));
      }

      const account = linkedUser?.email || row.email || response.uid;
      const credentialsRef = this.dialog.open(EmployeeTemporaryCredentialsDialogComponent, {
        width: '560px',
        maxWidth: '95vw',
        disableClose: true,
        autoFocus: false,
        data: {
          title: this.t('employeesCatalog.passwordReset.title'),
          description: this.t('employeesCatalog.passwordReset.description'),
          account,
          accountLabel: account === response.uid
            ? this.t('usersCatalog.fields.uid')
            : this.t('employeesCatalog.fields.email'),
          password: response.temporaryPassword,
          mustChangePassword: response.mustChangePassword === true,
        },
      });
      await firstValueFrom(credentialsRef.afterClosed());
      this.showNotification(
        'snackbar-success',
        this.t('employeesCatalog.notifications.passwordReset'),
        'bottom',
        'center'
      );
    } catch (error) {
      console.error('Error resetting employee user password from list', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('employeesCatalog.notifications.resetPasswordError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  private getLinkedUserUid(row?: EmployeeRecord): string {
    if (!row) {
      return '';
    }
    return (
      row.userUid ||
      this.users.find((user) => user.employeeId === row.employeeId)?.uid ||
      ''
    );
  }

  private getUserLinkOptions(employeeId: string, currentUserUid = ''): Array<{ label: string; value: string }> {
    const availableUsers = this.users
      .filter((user) => user.uid === currentUserUid || user.status !== 'disabled')
      .filter((user) => user.uid === currentUserUid || !user.employeeId || user.employeeId === employeeId)
      .sort((left, right) =>
        `${left.displayName || ''} ${left.email || ''}`.localeCompare(`${right.displayName || ''} ${right.email || ''}`)
      );

    return [
      {
        label: this.t('employeesCatalog.fields.noRelatedUser'),
        value: '',
      },
      ...availableUsers.map((user) => ({
        label: [user.displayName || user.uid, user.email ? `· ${user.email}` : ''].join(' ').trim(),
        value: user.uid,
      })),
    ];
  }

  private async loadDevicesForCompany(companyId: string): Promise<DeviceRecord[]> {
    if (!companyId) {
      return [];
    }

    try {
      const response = await firstValueFrom(
        this.apiService.get<DevicesResponse>('devices', {
          params: {
            companiesRecordId: companyId,
            perPage: 200,
            page: 1,
          },
        })
      );

      return (response?.data || [])
        .filter((device) => device.isActive !== false)
        .sort((left, right) => (left.name || left.serial || '').localeCompare(right.name || right.serial || ''));
    } catch (error) {
      console.error('Error loading devices for fingerprint enrollment', error);
      return [];
    }
  }

  private hasAccess(action: string): boolean {
    return this.authorizationService.hasAccess({ resource: 'employees', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2200,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}
